import { colors } from '@creditornot/cb-ingredients';
import { MenuAccordionItem } from '@creditornot/cb-menu';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { Active } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';

import { Category } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { localize } from 'common/utils';
import LazyLoadedThumbnail from 'modules/menu/components/LazyLoadedThumbnail';
import { MenuEditorDragData } from 'modules/menu/components/MenuEditorDnDContext';
import { getMenuEditorDragEventData } from 'modules/menu/components/MenuEditorDnDContext/utils';
import { solidify } from 'modules/menu/solidify';

import SubcategoryNavItems from './SubcategoryNavItems';

type DnDCategoryNavAccordionItem = {
  menuEditorRootUrl: string;
  language: string;
  hasCategoryImages: boolean;
  category: Category;
  isDroppable?: boolean;
  disabledSubcategorySort: boolean;
};

const CategoryNavItemDragOverlay = React.memo(styled(MenuAccordionItem).attrs({
  caretPosition: 'right',
})`
  background-color: ${cssThemedValue({
    light: solidify(colors.pepper8, 'light'),
    dark: solidify(colors.salt8, 'dark'),
  })} !important;
`);

const Root = styled.div<{ isDragging?: boolean }>`
  margin-inline-end: -8px;
  margin-inline-start: -8px;
  padding-block-start: 4px;
  padding-block-end: 4px;
  opacity: ${({ isDragging }) => (isDragging ? 0.6 : 1)};
`;

const ItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const useMenuAccordionItemController = ({
  path,
  active,
  isOver,
  categoryId,
}: {
  path: string;
  categoryId: string;
  active: null | Active;
  isOver: boolean;
}) => {
  const history = useHistory();
  const match = useRouteMatch({
    path,
  });
  const [isOpen, setIsOpen] = useState(!!match);
  const toggle = useCallback(() => {
    setIsOpen(isOpenState => (match?.isExact ? !isOpenState : true));

    history.push({
      pathname: path,
    });
  }, [history, match?.isExact, path]);

  useEffect(() => {
    const activeEvent = active && getMenuEditorDragEventData(active);
    if (!activeEvent) {
      return;
    }

    const isDraggingSubcategory =
      activeEvent.type === 'DND_NAVIGATION_SUBCATEGORY' && activeEvent.parentCategoryId !== categoryId;

    const isDraggingItem = [
      'DND_EDITOR_SINGLE_ITEM',
      'DND_EDITOR_MULTIPLE_ITEMS',
      'DND_EDITOR_SINGLE_NO_CATEGORY_ITEM',
      'DND_EDITOR_MULTIPLE_NO_CATEGORY_ITEMS',
    ].includes(activeEvent.type);

    if ((isDraggingSubcategory || isDraggingItem) && isOver && !isOpen) {
      setIsOpen(true);
    }
  }, [active, categoryId, isOpen, isOver, setIsOpen]);

  return {
    isOpen,
    toggle,
    setIsOpen,
    active: !!match?.isExact,
  };
};

export const DnDCategoryNavAccordionItem = ({
  category,
  menuEditorRootUrl,
  hasCategoryImages,
  language,
  disabledSubcategorySort,
}: DnDCategoryNavAccordionItem) => {
  const data: MenuEditorDragData = useMemo(
    () => ({
      type: 'DND_ACCORDION_NAVIGATION_CATEGORY',
      category,
      dragOverlay: (
        <Root>
          <CategoryNavItemDragOverlay>{localize(category.name, language)}</CategoryNavItemDragOverlay>
        </Root>
      ),
      shrinkOverlayTypes: [
        'DND_EDITOR_SINGLE_ITEM',
        'DND_EDITOR_MULTIPLE_ITEMS',
        'DND_EDITOR_SINGLE_NO_CATEGORY_ITEM',
        'DND_EDITOR_MULTIPLE_NO_CATEGORY_ITEMS',
      ],
    }),
    [category, language],
  );
  const sortable = useSortable({
    id: category.id,
    data,
  });
  const { isOpen, toggle, active } = useMenuAccordionItemController({
    path: `${menuEditorRootUrl}/${category.id}`,
    isOver: sortable.isOver,
    active: sortable.active,
    categoryId: category.id,
  });

  const dndStyle = useMemo(
    () => ({
      transform: CSS.Transform.toString(sortable.transform),
      transition: sortable.transition,
    }),
    [sortable.transform, sortable.transition],
  );

  return (
    <Root style={dndStyle} isDragging={sortable.isDragging} ref={sortable.setNodeRef} {...sortable.attributes}>
      <MenuAccordionItem
        {...sortable.listeners}
        caretPosition="right"
        open={isOpen}
        onClick={toggle}
        active={active}
        accordionContent={
          <SortableContext
            disabled={disabledSubcategorySort || !isOpen}
            strategy={verticalListSortingStrategy}
            items={category.child_categories.map(({ id }) => id)}
          >
            <SubcategoryNavItems
              menuEditorRootUrl={menuEditorRootUrl}
              language={language}
              hasCategoryImages={hasCategoryImages}
              subcategories={category.child_categories}
              categoryId={category.id}
            />
          </SortableContext>
        }
      >
        <ItemContent>
          {category.image && (
            <LazyLoadedThumbnail size="tiny" alt="" blurhash={category.image_blurhash ?? null} src={category.image} />
          )}
          {localize(category.name, language)}
        </ItemContent>
      </MenuAccordionItem>
    </Root>
  );
};
